import { UserRole } from '@/shared/constants/user-roles'

/**
 * Whether the current user role participates in privacy masking at all
 * (Volunteers, Supporters, and Homeowners). Others (Admin, BaseLeader, etc.) never mask.
 */
export function shouldMaskHomeownerPrivacy(role: string | null | undefined): boolean {
  if (!role) return false
  return (
    role === UserRole.VOLUNTEER ||
    role === UserRole.Supporter ||
    role === UserRole.HOMEOWNER
  )
}

/**
 * Whether we should mask this specific homeowner's data for the current user.
 * - Volunteer / Supporter: always mask.
 * - Homeowner: mask everyone except their own (session sub matches homeowner user id).
 * - Other roles: never mask.
 */
export function shouldMaskHomeownerData(
  role: string | null | undefined,
  sessionSub: string | number | null | undefined,
  homeownerUserId: number | null | undefined
): boolean {
  if (!shouldMaskHomeownerPrivacy(role)) return false
  if (role === UserRole.HOMEOWNER) {
    if (sessionSub == null || homeownerUserId == null) return true
    if (String(sessionSub) === String(homeownerUserId)) return false
  }
  return true
}

/**
 * Masks the last name for privacy.
 * - "First Last" → "First L***", "Only" → "O***".
 * - If the name contains " - " (e.g. "Lindsey Barnett - New Day"), show only the
 *   first name and mask the rest as " ****" → "Lindsey ****".
 * Returns original if empty/null/undefined.
 */
export function maskHomeownerLastName(fullname: string | null | undefined): string {
  if (fullname == null || fullname.trim() === '') return fullname ?? ''
  const t = fullname.trim()

  // Dash case: "Lindsey Barnett - New Day" → "Lindsey ****"
  if (t.includes(' - ')) {
    const beforeDash = t.split(' - ')[0]!.trim()
    const parts = beforeDash.split(/\s+/)
    const first = parts[0]
    if (first) return `${first} ****`
    return '****'
  }

  // No dash: "First Last" → "First L***", "Only" → "O***"
  const parts = t.split(/\s+/)
  if (parts.length >= 2) {
    const last = parts[parts.length - 1]!
    const masked = last.charAt(0) + '***'
    return [...parts.slice(0, -1), masked].join(' ')
  }
  return t.charAt(0) + '***'
}

/**
 * Masks phone for privacy: shows last 2 digits, e.g. "(***) ***-**41".
 * Returns "—" or "N/A" when empty; otherwise masked string.
 */
export function maskHomeownerPhone(phone: string | null | undefined): string {
  if (phone == null || phone.trim() === '') return '—'
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 10) return '(***) ***-**XX'
  const last2 = digits.slice(-2)
  return `(***) ***-**${last2}`
}

/**
 * Masks the street portion of an address for privacy; leaves city, state, zip visible.
 * e.g. "113 S Mauney Ave, Old Fort, NC 28762" → "****, Old Fort, NC 28762".
 * Returns "—" when empty; otherwise masked string.
 */
export function maskHomeownerAddress(address: string | null | undefined): string {
  if (address == null || address.trim() === '') return '—'
  const t = address.trim()
  const parts = t.split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length >= 2) {
    return ['****', ...parts.slice(1)].join(', ')
  }
  if (parts.length === 1) return '****'
  return '—'
}
